import React, { FC, Fragment, useState } from 'react';
import { Trans } from 'react-i18next';
import {
  AppBar,
  Button,
  Divider,
  Grid,
  IconButton,
  Toolbar,
  SwipeableDrawer,
  Typography,
} from '@material-ui/core';
import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import NotificationsNoneIcon from '@material-ui/icons/NotificationsNone';
import MenuIcon from '@material-ui/icons/Menu';

import { ClientLogo, CustomSVG, Desktop, Tablet, Phone } from 'components';
import { Activity, Index, Groups, Leaderboard, People, Admin } from 'Links';
import { isPathMatch } from 'utilities';
import { isAdminUser } from 'helpers';
import { useCurrentUser, useFeatures } from 'hooks';
import {
  HOME_TAB_ROUTES,
  GROUPS_TAB_ROUTES,
  PEOPLE_TAB_ROUTES,
  ACTIVITY_TAB_ROUTES,
  LEADERBOARD_TAB_ROUTES,
  ADMIN_TAB_ROUTES,
} from 'paths';

import Search from './Search';
import Notifications from './Notifications';
import User from './User';
import { ILink } from '../types';

import Drawer from '../components/Bar/Drawer';

interface IGetLinks {
  isAdmin: boolean;
  hasLeaderboard: boolean;
}

const styles = (theme: Theme) =>
  createStyles({
    logo: {
      display: 'flex',
      alignItems: 'center',
      paddingRight: theme.spacing(2),
    },
    item: {
      display: 'flex',
      alignItems: 'center',
    },
    divider: {
      width: 1,
      height: theme.mixins.toolbar.minHeight,
      marginRight: theme.spacing(1),
    },
    button: {
      fontWeight: theme.typography.fontWeightBold,
      fontSize: theme.typography.pxToRem(14),
      transition: 'color 100ms ease-in-out',
      '&:hover &:not($buttonActive)': {
        '& svg': {
          color: theme.palette.text.primary,
        },
      },
    },
    buttonActive: {
      color: theme.palette.text.primary,
      '& svg': {
        color: theme.palette.secondary.main,
      },
    },
    menuButton: {
      marginLeft: -12,
    },
  });

const getLinks = ({ isAdmin, hasLeaderboard }: IGetLinks) => {
  const links: ILink[] = [
    { icon: 'home', i18nKey: 'common:home', component: Index, matchPaths: HOME_TAB_ROUTES },
    {
      icon: 'groups',
      i18nKey: 'common:group_plural',
      component: Groups,
      matchPaths: GROUPS_TAB_ROUTES,
    },
    {
      icon: 'people',
      i18nKey: 'common:person_plural',
      component: People,
      matchPaths: PEOPLE_TAB_ROUTES,
    },
    {
      icon: 'activity',
      i18nKey: 'common:activity',
      component: Activity,
      matchPaths: ACTIVITY_TAB_ROUTES,
    },
  ];

  if (hasLeaderboard) {
    links.push({
      icon: 'star',
      i18nKey: 'common:leaderboard',
      component: Leaderboard,
      matchPaths: LEADERBOARD_TAB_ROUTES,
    });
  }

  if (isAdmin) {
    links.push({
      icon: 'admin',
      i18nKey: 'common:admin',
      component: Admin,
      matchPaths: ADMIN_TAB_ROUTES,
    });
  }

  return links;
};

const Bar: FC<WithStyles<typeof styles>> = ({ classes }) => {
  const [notificationsLoaded, setNotificationsLoaded] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const currentUser = useCurrentUser();
  const { leaderboard } = useFeatures();
  const links = getLinks({ isAdmin: isAdminUser(currentUser), hasLeaderboard: leaderboard });

  function renderButton(link: ILink) {
    const { icon, i18nKey, component, matchPaths } = link;
    const isActive = isPathMatch(matchPaths, window.location.pathname);

    const buttonClasses = classNames(classes.button, {
      [classes.buttonActive]: isActive,
    });

    return (
      <Grid key={i18nKey} className={classes.item} item>
        <Divider className={classes.divider} />
        <Button
          id={`menu-item-${i18nKey.replace(':', '-')}`}
          className={buttonClasses}
          component={component}
          startIcon={<CustomSVG name={icon} />}>
          <Trans i18nKey={i18nKey} />
        </Button>
      </Grid>
    );
  }

  function renderLogo() {
    return (
      <Typography className={classes.logo} component={Index} color="inherit">
        <ClientLogo disableMargin height={24} />
      </Typography>
    );
  }

  function renderMenuButton() {
    return (
      <IconButton
        id="mobileMenuButton"
        className={classes.menuButton}
        onClick={() => setDrawerOpen(true)}>
        <MenuIcon />
      </IconButton>
    );
  }

  function renderNotifications() {
    return (
      <Fragment>
        <Notifications onLoaded={() => setNotificationsLoaded(true)} />
        {!notificationsLoaded && (
          <IconButton>
            <NotificationsNoneIcon />
          </IconButton>
        )}
      </Fragment>
    );
  }

  function renderDrawer() {
    return (
      <SwipeableDrawer
        anchor="left"
        onClose={() => setDrawerOpen(false)}
        onOpen={() => setDrawerOpen(true)}
        open={drawerOpen}>
        <Drawer
          links={links}
          search={<Search sidebar />}
          onNavigate={() => {
            setDrawerOpen(false);
          }}
        />
      </SwipeableDrawer>
    );
  }

  function renderDesktop() {
    return (
      <Desktop>
        {renderLogo()}
        <Grid container spacing={1} justify="flex-end">
          <Grid className={classes.item} item xs>
            <Divider className={classes.divider} />
            <Search />
          </Grid>
          {links.map(renderButton)}
          <Grid className={classes.item} item>
            <Divider className={classes.divider} />
            {renderNotifications()}
          </Grid>
          <Grid item className={classes.item}>
            <Divider className={classes.divider} />
            <User />
          </Grid>
        </Grid>
      </Desktop>
    );
  }

  function renderTablet() {
    return (
      <Tablet>
        {renderDrawer()}
        {renderMenuButton()}
        {renderLogo()}
        <Grid container spacing={1} justify="flex-end">
          <Grid className={classes.item} item xs>
            <Divider className={classes.divider} />
            <Search />
          </Grid>
          <Grid className={classes.item} item>
            <Divider className={classes.divider} />
            {renderNotifications()}
          </Grid>
          <Grid item className={classes.item}>
            <User />
          </Grid>
        </Grid>
      </Tablet>
    );
  }

  function renderMobile() {
    return (
      <Phone>
        {renderDrawer()}
        {renderMenuButton()}
        {renderLogo()}
        <Grid container spacing={1} justify="flex-end">
          <Grid className={classes.item} item>
            {renderNotifications()}
          </Grid>
          <Grid item className={classes.item}>
            <User />
          </Grid>
        </Grid>
      </Phone>
    );
  }

  return (
    <AppBar position="fixed" color="default">
      <Toolbar>
        {renderDesktop()}
        {renderTablet()}
        {renderMobile()}
      </Toolbar>
    </AppBar>
  );
};

export default withStyles(styles)(Bar);
